import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { ExternalLink, CheckCircle2, AlertTriangle, Users, ClipboardList, Lightbulb } from "lucide-react";

const AUTHORITY_DOMAINS = [
  "europa.eu", "fda.gov", "iso.org", "ema.europa.eu",
  "who.int", "ich.org", "edqm.eu",
];

/** Icon map for common regulatory h2 headings */
const SECTION_ICONS: Record<string, React.ReactNode> = {
  "main takeaways": <CheckCircle2 size={22} className="text-qbd-blue" strokeWidth={1.6} />,
  "key takeaways": <CheckCircle2 size={22} className="text-qbd-blue" strokeWidth={1.6} />,
  "who is affected": <Users size={22} className="text-qbd-blue" strokeWidth={1.6} />,
  "what mahs should do now": <ClipboardList size={22} className="text-qbd-blue" strokeWidth={1.6} />,
  "what you should do now": <ClipboardList size={22} className="text-qbd-blue" strokeWidth={1.6} />,
  "how we can support": <Lightbulb size={22} className="text-qbd-blue" strokeWidth={1.6} />,
  "why is it relevant": <AlertTriangle size={22} className="text-amber-500" strokeWidth={1.6} />,
};

function getSectionIcon(heading: string) {
  const key = heading.toLowerCase().trim();
  return SECTION_ICONS[key] || null;
}

interface Section {
  heading: string;
  level: 2 | 3;
  body: string;
}

/** Strip HubSpot CTA artifacts from markdown */
function stripHubspotCtas(md: string): string {
  // Remove linked CTA images: [![alt](hubspot-img)](hubspot-url)
  let cleaned = md.replace(/\[!\[[^\]]*\]\([^)]*no-cache\.hubspot\.com[^)]*\)\]\([^)]*\)/g, "");
  // Remove standalone CTA images: ![alt](hubspot-cta-url)
  cleaned = cleaned.replace(/!\[[^\]]*\]\([^)]*no-cache\.hubspot\.com[^)]*\)/g, "");
  // Remove linked CTA images with hubspot redirect URLs
  cleaned = cleaned.replace(/\[!\[[^\]]*\]\([^)]*\)\]\([^)]*(?:qbdgroup\.com\/hs\/cta|hubspot\.com\/web-interactives|cta-redirect\.hubspot\.com|cta-service-cms2\.hubspot\.com)[^)]*\)/g, "");
  // Remove ## headings that are just CTA links (e.g. ## [![...](...)](/...))
  cleaned = cleaned.replace(/^##\s*\[!\[.*$/gm, "");
  return cleaned;
}

/** Parse markdown into h2/h3 sections */
function parseSections(md: string): { intro: string; h2Sections: { heading: string; intro: string; cards: Section[] }[] } {
  const lines = md.split("\n");
  let intro = "";
  const h2Sections: { heading: string; intro: string; cards: Section[] }[] = [];
  let currentH2: { heading: string; intro: string; cards: Section[] } | null = null;
  let currentH3: Section | null = null;
  let buffer: string[] = [];

  const flushBuffer = () => {
    const text = buffer.join("\n").trim();
    buffer = [];
    return text;
  };

  for (const line of lines) {
    const h2Match = line.match(/^## (.+)$/);
    const h3Match = line.match(/^### (.+)$/);

    if (h2Match) {
      // Flush previous h3
      if (currentH3) {
        currentH3.body = flushBuffer();
        currentH2?.cards.push(currentH3);
        currentH3 = null;
      } else if (currentH2) {
        const text = flushBuffer();
        if (text) currentH2.intro += (currentH2.intro ? "\n\n" : "") + text;
      } else {
        intro = flushBuffer();
      }
      // Start new h2
      currentH2 = { heading: h2Match[1], intro: "", cards: [] };
      h2Sections.push(currentH2);
    } else if (h3Match && currentH2) {
      // Flush previous h3
      if (currentH3) {
        currentH3.body = flushBuffer();
        currentH2.cards.push(currentH3);
      } else {
        const text = flushBuffer();
        if (text) currentH2.intro += (currentH2.intro ? "\n\n" : "") + text;
      }
      currentH3 = { heading: h3Match[1], level: 3, body: "" };
    } else {
      buffer.push(line);
    }
  }

  // Flush remaining
  if (currentH3) {
    currentH3.body = flushBuffer();
    currentH2?.cards.push(currentH3);
  } else if (currentH2) {
    const text = flushBuffer();
    if (text) currentH2.intro += (currentH2.intro ? "\n\n" : "") + text;
  } else {
    intro = flushBuffer();
  }

  return { intro, h2Sections };
}

const sharedComponents = {
  a: ({ href, children }: any) => {
    // Strip HubSpot CTA links entirely
    const isHubspotCta = href && (href.includes('qbdgroup.com/hs/cta/') || href.includes('hubspot.com/web-interactives/') || href.includes('cta-redirect.hubspot.com') || href.includes('cta-service-cms2.hubspot.com'));
    if (isHubspotCta) return null;

    const isAuthority = href
      ? AUTHORITY_DOMAINS.some((d) => new URL(href, "https://x.com").hostname.endsWith(d))
      : false;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-qbd-blue hover:underline decoration-accent/40 underline-offset-[3px] transition-colors">
        {children}
        {isAuthority && <ExternalLink size={12} className="inline ml-1 -mt-0.5 opacity-60" />}
      </a>
    );
  },
  img: ({ src, alt }: any) => {
    // Strip HubSpot CTA images entirely
    if (src && /no-cache\.hubspot\.com\/cta\/default\//.test(src)) {
      return null;
    }
    return (
      <figure className="my-8 -mx-4 sm:mx-0">
        <div className="overflow-hidden rounded-2xl shadow-lg shadow-primary/5">
          <img src={src} alt={alt || ""} className="w-full" loading="lazy" />
        </div>
        {alt && <figcaption className="mt-3 text-center text-xs text-muted-foreground italic">{alt}</figcaption>}
      </figure>
    );
  },
  ul: ({ children }: any) => <ul className="space-y-2 [&_li]:relative [&_li]:pl-2">{children}</ul>,
  ol: ({ children }: any) => <ol className="space-y-2 [&_li]:relative [&_li]:pl-2">{children}</ol>,
};

const proseClasses = "prose prose-base max-w-none prose-headings:text-foreground prose-p:text-foreground/80 prose-p:leading-[1.8] prose-p:text-[0.9375rem] prose-a:text-qbd-blue prose-strong:text-foreground prose-strong:font-semibold prose-li:text-foreground/80 prose-li:leading-[1.8] prose-li:text-[0.9375rem] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:marker:text-primary/60 [&_p+p]:!mt-5 [&_p+ul]:!mt-4 [&_p+ol]:!mt-4 [&_ul]:!mb-0 [&_ol]:!mb-0 [&_ul+p]:!mt-8 [&_ol+p]:!mt-8 [&_ul+ul]:!mt-6 [&_li]:!my-0 [&_li+li]:!mt-2.5 [&_h3]:!mb-4";

interface Props {
  content: string;
}

const RegulatoryUpdateContent = ({ content }: Props) => {
  const { intro, h2Sections } = parseSections(stripHubspotCtas(content));

  return (
    <div className="space-y-12">
      {/* Intro paragraph */}
      {intro && (
        <div className={proseClasses}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={sharedComponents}>
            {intro}
          </ReactMarkdown>
        </div>
      )}

      {h2Sections.map((section, si) => {
        const icon = getSectionIcon(section.heading);
        const hasCards = section.cards.length > 0;
        const isActionSection = /what.*should|how we can/i.test(section.heading);

        return (
          <div key={si}>
            {/* H2 heading */}
            <div className="flex items-center gap-3 mb-6">
              {icon}
              <h2
                id={section.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}
                className="text-[1.5rem] sm:text-[1.75rem] font-bold text-foreground"
              >
                {section.heading}
              </h2>
            </div>

            {/* H2 intro text */}
            {section.intro && (
              <div className={`${proseClasses} ${hasCards ? 'mb-5' : ''}`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={sharedComponents}>
                  {section.intro}
                </ReactMarkdown>
              </div>
            )}

            {/* H3 cards */}
            {hasCards && (
              <div className="space-y-4">
                {section.cards.map((card, ci) => (
                  <div
                    key={ci}
                    className={`rounded-2xl p-5 sm:p-6 border transition-colors ${
                      isActionSection
                        ? 'bg-primary/[0.03] border-primary/10'
                        : 'bg-secondary/40 border-border/30'
                    }`}
                  >
                    <h3
                      id={card.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}
                      className="text-lg sm:text-xl font-bold text-qbd-blue mb-2"
                    >
                      {isActionSection && /^\d+\./.test(card.heading)
                        ? card.heading
                        : card.heading}
                    </h3>
                    {card.body && (
                      <div className={proseClasses}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={sharedComponents}>
                          {card.body}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RegulatoryUpdateContent;
